import { getLoadRequestDetailsByID } from "apis/myRequestsService";

export interface RoutableNotification {
  type?: string;
  requestDetailId?: number | null;
}

export interface NotificationTarget {
  path: string;
  state?: Record<string, unknown>;
}

// "/my-loads" (YourRequest.tsx) matches rows by LoadRequest.id, not by
// requestDetailId — those are two different IDs on the same record (see
// LoadsFlightListing.tsx, which looks up requestDetailId but navigates with
// flight.id). Notifications only carry requestDetailId, so resolve it to the
// LoadRequest.id via the existing "get request details" endpoint first.
const resolveLoadRequestId = async (
  requestDetailId: number,
): Promise<number | null> => {
  try {
    const data: any = await getLoadRequestDetailsByID(requestDetailId);
    const resolved = data?.requestId ?? data?.id ?? null;
    return resolved != null ? Number(resolved) : null;
  } catch (error) {
    console.error(
      `Failed to resolve LoadRequest.id for requestDetailId ${requestDetailId}:`,
      error,
    );
    return null;
  }
};

// Routes a notification straight to the page/record it's about, based on the
// `type` field the backend writes on each notification doc. Unknown/missing
// types (or a failed ID resolution) fall back to the notifications list
// rather than guessing.
export const resolveNotificationTarget = async (
  notification: RoutableNotification,
): Promise<NotificationTarget | null> => {
  const requestDetailId = notification.requestDetailId;

  switch (notification.type) {
    case "request_answered": {
      if (!requestDetailId) return null;
      const loadRequestId = await resolveLoadRequestId(requestDetailId);
      if (loadRequestId == null) return null;
      return { path: "/my-loads", state: { targetRequestId: loadRequestId } };
    }
    case "updated_answer_available": {
      if (!requestDetailId) return null;
      const loadRequestId = await resolveLoadRequestId(requestDetailId);
      if (loadRequestId == null) return null;
      return { path: "/my-loads", state: { targetRequestId: loadRequestId } };
    }
    default:
      return null;
  }
};
